/**
 * Redux Middleware for Async Processing
 * Intercepts actions with large datasets and processes them asynchronously
 */

import { Middleware } from 'redux';
import { processArrayToKeyedObject, shouldUseAsyncProcessing } from './asyncReducerHelper';
import { cleanPart } from '../reducers/partsReducer';
import {
    GET_ALL_PARTS_SUCCESS,
    MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS,
    GET_ALL_PARTS_ASYNC_COMPLETE,
    MANAGE_MEASUREMENT_POINT_LISTS_ASYNC_COMPLETE,
    REMOVE_INITIAL_APP_SYNC_ITEM,
    UPDATE_SYNC_ITEM_PROGRESS,
    INSTALL_ADD_SUCCESS
} from '../actions/actionTypes';

// Track ongoing async operations to prevent duplicates
const ongoingOperations = new Set<string>();

export const asyncProcessingMiddleware: Middleware = store => next => action => {
    // Check if this is an action that needs async processing
    if (action.type === GET_ALL_PARTS_SUCCESS) {
        const parts = action.payload?.data?.parts || action.payload?.parts || [];
        const skipAsync = action.payload?.skipAsyncProcessing === true;

        if (Array.isArray(parts) && shouldUseAsyncProcessing(parts.length) && !skipAsync) {
            // Prevent duplicate processing
            if (ongoingOperations.has('parts')) {
                return next(action);
            }

            ongoingOperations.add('parts');

            // Process asynchronously with progress updates
            processArrayToKeyedObject({
                items: parts,
                processor: cleanPart,
                chunkSize: 25,
                onProgress: (processed: number, total: number) => {
                    // Report incremental progress during parts processing
                    store.dispatch({
                        type: UPDATE_SYNC_ITEM_PROGRESS,
                        name: 'Parts',
                        recordsProcessed: processed,
                        recordCount: total
                    });
                }
            })
                .then(keyedParts => {
                    ongoingOperations.delete('parts');

                    // Dispatch completion action with processed data
                    store.dispatch({
                        type: GET_ALL_PARTS_ASYNC_COMPLETE,
                        payload: {
                            ...action.payload,
                            processedParts: keyedParts
                        },
                        updateDate: action.updateDate
                    });

                    // Report final completion
                    store.dispatch({
                        type: UPDATE_SYNC_ITEM_PROGRESS,
                        name: 'Parts',
                        recordsProcessed: parts.length,
                        recordCount: parts.length
                    });

                    // Now that Redux processing is complete, remove from sync list
                    store.dispatch({
                        type: REMOVE_INITIAL_APP_SYNC_ITEM,
                        name: 'Parts'
                    });
                })
                .catch(error => {
                    ongoingOperations.delete('parts');
                    // Fall back to original action
                    next(action);
                });

            // Return a modified action that indicates async processing started
            return next({
                ...action,
                type: GET_ALL_PARTS_SUCCESS,
                payload: {
                    ...action.payload,
                    asyncProcessing: true,
                    data: {
                        ...action.payload.data,
                        parts: [] // Empty array to prevent sync processing
                    }
                }
            });
        }
    }

    if (action.type === MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS) {
        const measurementPointLists = action.allLists || [];

        if (Array.isArray(measurementPointLists) && shouldUseAsyncProcessing(measurementPointLists.length)) {
            // Prevent duplicate processing
            if (ongoingOperations.has('measurementPoints')) {
                return next(action);
            }

            ongoingOperations.add('measurementPoints');

            // Process asynchronously with progress updates - NOTE: Keep raw data for measurement points!
            processArrayToKeyedObject({
                items: measurementPointLists,
                processor: (item: any) => item, // Keep raw data - cleaning happens in reducer
                chunkSize: 25,
                onProgress: (processed: number, total: number) => {
                    // Report incremental progress during measurement point lists processing
                    store.dispatch({
                        type: UPDATE_SYNC_ITEM_PROGRESS,
                        name: 'MeasurementPointLists',
                        recordsProcessed: processed,
                        recordCount: total
                    });
                }
            })
                .then(keyedMeasurementPointLists => {
                    ongoingOperations.delete('measurementPoints');

                    // Dispatch completion action with processed data
                    store.dispatch({
                        type: MANAGE_MEASUREMENT_POINT_LISTS_ASYNC_COMPLETE,
                        payload: {
                            ...action.payload,
                            processedMeasurementPointLists: keyedMeasurementPointLists
                        },
                        updateDate: action.updateDate
                    });

                    // Report final completion
                    store.dispatch({
                        type: UPDATE_SYNC_ITEM_PROGRESS,
                        name: 'MeasurementPointLists',
                        recordsProcessed: measurementPointLists.length,
                        recordCount: measurementPointLists.length
                    });

                    // Now that Redux processing is complete, remove from sync list
                    store.dispatch({
                        type: REMOVE_INITIAL_APP_SYNC_ITEM,
                        name: 'MeasurementPointLists'
                    });
                })
                .catch(error => {
                    ongoingOperations.delete('measurementPoints');
                    // Fall back to original action
                    next(action);
                });

            // Return a modified action that indicates async processing started
            return next({
                ...action,
                type: MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS,
                allLists: [], // Empty array to prevent sync processing
                payload: {
                    ...action.payload,
                    asyncProcessing: true
                }
            });
        }
    }

    // Handle INSTALL_ADD_SUCCESS for cloning operations
    if (action.type === INSTALL_ADD_SUCCESS && action.isCloning) {
        // Import getLatestHelper dynamically to avoid circular dependencies
        import('../actions/commonActions').then(({ getLatestHelper }) => {
            if (action.facilityID && action.installIds) {
                // Get current state to determine lastSync
                const state = store.getState();
                const lastSync = state.syncStatus.lastSync || 0;

                // Create cloning-specific getLatest call
                const cloningLatestHelper = (dispatch: any) => {
                    const axiosOptions = {
                        method: 'post' as const,
                        data: {
                            updated: new Date(lastSync * 1000).toISOString(),
                            JobIDs: [],
                            Cloning: true,
                            FacilityID: action.facilityID,
                            InstallBaseIds: action.installIds
                        }
                    };

                    return import('../components/auth/Auth-Utils').then(({ msalFetch }) => {
                        return import('../constants/apiEndpoints').then(apiModule => {
                            return msalFetch(apiModule.default.update.getLatest, axiosOptions).then((data: any) => {
                                if (data.data) {
                                    const {
                                        jobs,
                                        installBases,
                                        facilities,
                                        measurementPointLists,
                                        measurementPointListResults,
                                        products,
                                        codes,
                                        simpleMeasurementPointListResults
                                    } = data.data;

                                    // Process the response the same way as getLatestHelper
                                    if (jobs && jobs.length) {
                                        import('../actions/manageJobActions').then(({ receiveUpdatedJobs }) => {
                                            dispatch(receiveUpdatedJobs(jobs));
                                        });
                                    }
                                    if (facilities && facilities.length) {
                                        import('../actions/actionTypes').then(types => {
                                            dispatch({
                                                type: types.GET_FACILITIES_SUCCESS,
                                                facilities
                                            });
                                        });
                                    }
                                    if (installBases && installBases.length) {
                                        import('../actions/actionTypes').then(types => {
                                            dispatch({
                                                type: types.GET_INVENTORY_SUCCESS,
                                                installBases
                                            });
                                        });
                                    }
                                    if (products && products.length) {
                                        import('../actions/actionTypes').then(types => {
                                            dispatch({ type: types.PRODUCT_UPDATES, products });
                                        });
                                    }
                                    if (codes && codes.length) {
                                        import('../actions/actionTypes').then(types => {
                                            dispatch({
                                                type: types.MANAGE_JOB_COMMENT_CODES_SUCCESS,
                                                codes
                                            });
                                        });
                                    }
                                    if (measurementPointLists && measurementPointLists.length) {
                                        import('../actions/actionTypes').then(types => {
                                            measurementPointLists.forEach((measurementPointList: any) => {
                                                dispatch({
                                                    type: types.MANAGE_MEASUREMENT_POINT_LIST_UPDATE,
                                                    measurementPointList
                                                });
                                            });
                                        });
                                    }
                                    if (measurementPointListResults && measurementPointListResults.length) {
                                        import('../actions/actionTypes').then(types => {
                                            import('moment').then(moment => {
                                                dispatch({
                                                    type: types.GET_MEASUREMENT_POINT_FACILITY_RESULTS_SUCCESS,
                                                    results: measurementPointListResults,
                                                    updateDate: moment.default().unix()
                                                });
                                            });
                                        });
                                    }
                                    if (simpleMeasurementPointListResults && simpleMeasurementPointListResults.length) {
                                        import('../actions/actionTypes').then(types => {
                                            import('moment').then(moment => {
                                                dispatch({
                                                    type: types.GET_SIMPLE_MEASUREMENT_POINT_JOB_RESULTS_SUCCESS,
                                                    results: simpleMeasurementPointListResults,
                                                    updateDate: moment.default().unix()
                                                });
                                            });
                                        });
                                    }
                                }
                            });
                        });
                    });
                };

                // Cast store.dispatch to handle thunk actions
                (store.dispatch as any)(cloningLatestHelper);
            }
        });
    }

    // For all other actions, proceed normally
    return next(action);
};
